import React, { useEffect, useState, useRef } from 'react';
import { CheckCircle2, Lock, Zap, ShieldCheck, CreditCard, X, Plus, BookOpen } from 'lucide-react';
type PlanType = 'sic' | 'packfull';
export function PricingSection() {
  const [isVisible, setIsVisible] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<PlanType>('packfull');
  const [showUpsellModal, setShowUpsellModal] = useState(false);
  const [addNticx, setAddNticx] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
      }
    }, {
      threshold: 0.1
    });
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => observer.disconnect();
  }, []);
  const isPackFull = selectedPlan === 'packfull';
  const ctaLinkSic = 'https://checkout.rincondigital.shop/cart/52917830517011:1';
  const ctaLinkPackFull = 'https://checkout.rincondigital.shop/cart/52917897363731:1';
  const ctaLinkPackFullWithNticx = 'https://checkout.rincondigital.shop/cart/52921028411667:1';
  const handleCtaClick = (e: React.MouseEvent) => {
    if (isPackFull) {
      e.preventDefault();
      setAddNticx(false);
      setShowUpsellModal(true);
    }
  };
  const handleUpsellContinue = () => {
    const url = addNticx ? ctaLinkPackFullWithNticx : ctaLinkPackFull;
    window.location.href = url;
  };
  return <section id="purchase" ref={sectionRef} className="py-16 bg-slate-50 relative overflow-hidden" data-id="element-162">
      <div className="max-w-3xl mx-auto px-4 relative z-10" data-id="element-163">
        {/* Section Header */}
        <div className="text-center mb-10" data-id="element-164">
          <h2 className={`text-4xl md:text-5xl font-black mb-4 text-slate-900 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'}`} data-id="element-165">
            Elegí tu plan
          </h2>
          <p className={`text-xl text-slate-600 transition-all duration-700 delay-100 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'}`} data-id="element-166">
            Todo lo que necesitás para enseñar sin estrés
          </p>
        </div>

        {/* Main Pricing Card (Dark) */}
        <div className={`bg-slate-900 rounded-3xl shadow-2xl overflow-hidden border border-slate-800 p-6 md:p-10 transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} data-id="element-167">
          <div className="space-y-4 mb-8" data-id="element-168">
            {/* Option 1: SIC 360 */}
            <label className={`relative flex flex-col sm:flex-row items-start sm:items-center justify-between p-5 md:p-6 rounded-2xl cursor-pointer transition-all duration-300 border-2 ${!isPackFull ? 'bg-slate-800 border-blue-500' : 'bg-slate-800/50 border-slate-700 hover:border-slate-600'}`} data-id="element-169">
              <div className="flex items-center gap-4 mb-4 sm:mb-0" data-id="element-170">
                {/* Radio Circle */}
                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${!isPackFull ? 'border-blue-500' : 'border-slate-500'}`} data-id="element-171">
                  {!isPackFull && <div className="w-3 h-3 rounded-full bg-blue-500" data-id="element-172" />}
                </div>

                <div data-id="element-173">
                  <div className="flex items-center gap-2 mb-1" data-id="element-174">
                    <span className="text-xl" data-id="element-175">📘</span>
                    <h3 className="text-xl font-black text-white" data-id="element-176">SIC 360</h3>
                  </div>
                  <p className="text-slate-400 text-sm" data-id="element-177">
                    Clases anuales resueltas + 4 bonos
                  </p>
                </div>
              </div>

              <div className="text-left sm:text-right ml-10 sm:ml-0" data-id="element-178">
                <p className="text-slate-500 line-through text-sm font-semibold mb-0.5" data-id="element-179">
                  $49.990
                </p>
                <p className="text-3xl font-black text-white" data-id="element-180">$19.990</p>
              </div>

              {/* Hidden radio input for accessibility */}
              <input type="radio" name="pricing-plan" value="sic" checked={!isPackFull} onChange={() => setSelectedPlan('sic')} className="sr-only" aria-label="Seleccionar plan SIC 360" data-id="element-181" />
            </label>

            {/* Option 2: PACK FULL 360 */}
            <label className={`relative flex flex-col sm:flex-row items-start sm:items-center justify-between p-5 md:p-6 rounded-2xl cursor-pointer transition-all duration-300 border-2 ${isPackFull ? 'bg-slate-800 border-orange-500 shadow-[0_0_20px_rgba(249,115,22,0.15)]' : 'bg-slate-800/50 border-slate-700 hover:border-slate-600'}`} data-id="element-182">
              <div className="flex items-start gap-4 mb-4 sm:mb-0" data-id="element-183">
                {/* Radio Circle */}
                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-1 transition-colors ${isPackFull ? 'border-orange-500' : 'border-slate-500'}`} data-id="element-184">
                  {isPackFull && <div className="w-3 h-3 rounded-full bg-orange-500" data-id="element-185" />}
                </div>

                <div data-id="element-186">
                  <div className="flex flex-wrap items-center gap-2 mb-2" data-id="element-187">
                    <span className="text-xl" data-id="element-188">🏆</span>
                    <h3 className="text-xl font-black text-white" data-id="element-189">
                      PACK FULL 360
                    </h3>
                  </div>

                  {/* Badges */}
                  <div className="flex flex-wrap gap-2 mb-2" data-id="element-190">
                    <span className="bg-orange-500 text-white text-xs font-bold px-2.5 py-1 rounded-md flex items-center gap-1" data-id="element-191">
                      ⭐ MÁS ELEGIDO
                    </span>
                    <span className="bg-green-600/20 text-green-400 border border-green-600/30 text-xs font-bold px-2.5 py-1 rounded-md" data-id="element-192">
                      AHORRÁS $30.000
                    </span>
                  </div>

                  <p className="text-slate-400 text-sm max-w-xs" data-id="element-193">
                    SIC 360 + Micro y Macro economía 360 + 100 Emprendimientos +
                    8 bonos
                  </p>
                </div>
              </div>

              <div className="text-left sm:text-right ml-10 sm:ml-0 self-start sm:self-center" data-id="element-194">
                <p className="text-slate-500 line-through text-sm font-semibold mb-0.5" data-id="element-195">
                  $69.990
                </p>
                <p className="text-3xl font-black text-orange-400" data-id="element-196">$39.990</p>
              </div>

              {/* Hidden radio input for accessibility */}
              <input type="radio" name="pricing-plan" value="packfull" checked={isPackFull} onChange={() => setSelectedPlan('packfull')} className="sr-only" aria-label="Seleccionar plan PACK FULL 360" data-id="element-197" />
            </label>
          </div>

          {/* Pack Full Details Module (Conditional) */}
          <div className={`transition-all duration-500 overflow-hidden ${isPackFull ? 'max-h-96 opacity-100 mb-8' : 'max-h-0 opacity-0 mb-0'}`} data-id="element-198">
            <div className="bg-slate-800/50 rounded-xl p-5 border border-slate-700" data-id="element-199">
              <p className="text-slate-300 text-sm font-bold mb-4 uppercase tracking-wider" data-id="element-200">
                ¿Qué incluye el Pack Full?
              </p>
              <ul className="space-y-3" data-id="element-201">
                <li className="flex items-start gap-3" data-id="element-202">
                  <span className="text-lg leading-none" data-id="element-203">📘</span>
                  <p className="text-slate-300 text-sm" data-id="element-204">
                    <span className="font-bold text-white" data-id="element-205">SIC 360:</span>{' '}
                    Clases anuales resueltas de SIC I y SIC II
                  </p>
                </li>
                <li className="flex items-start gap-3" data-id="element-206">
                  <span className="text-lg leading-none" data-id="element-207">📗</span>
                  <p className="text-slate-300 text-sm" data-id="element-208">
                    <span className="font-bold text-white" data-id="element-209">
                      Micro y Macro 360:
                    </span>{' '}
                    Economía completa para todo el año
                  </p>
                </li>
                <li className="flex items-start gap-3" data-id="element-210">
                  <span className="text-lg leading-none" data-id="element-211">💡</span>
                  <p className="text-slate-300 text-sm" data-id="element-212">
                    <span className="font-bold text-white" data-id="element-213">
                      100 Emprendimientos:
                    </span>{' '}
                    Ideas reales para trabajar con alumnos de último año
                  </p>
                </li>
              </ul>
            </div>
          </div>

          {/* CTA Button */}
          <div className="mb-8" data-id="element-214">
            <a href={isPackFull ? ctaLinkPackFull : ctaLinkSic} onClick={handleCtaClick} className={`block w-full font-black py-5 px-8 rounded-xl shadow-lg transform transition-all hover:scale-[1.02] text-center text-xl ${isPackFull ? 'bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'}`} data-id="element-215">
              {isPackFull ? 'SÍ, QUIERO PACK COMPLETO →' : 'QUIERO SIC 360 →'}
            </a>

            <div className={`transition-all duration-300 overflow-hidden ${isPackFull ? 'max-h-10 opacity-100 mt-4' : 'max-h-0 opacity-0 mt-0'}`} data-id="element-216">
              <p className="text-center text-slate-400 text-sm italic" data-id="element-217">
                La opción más elegida por docentes que enseñan en los dos
                niveles
              </p>
            </div>
          </div>

          {/* Trust Badges */}
          <div className="pt-6 border-t border-slate-800" data-id="element-218">
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-4 text-slate-400 text-sm font-medium" data-id="element-219">
              <div className="flex items-center gap-2" data-id="element-220">
                <Lock className="w-4 h-4 text-slate-500" data-id="element-221" />
                <span data-id="element-222">Pago seguro</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-223">
                <Zap className="w-4 h-4 text-yellow-500" data-id="element-224" />
                <span data-id="element-225">Acceso en 5 min</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-226">
                <ShieldCheck className="w-4 h-4 text-slate-500" data-id="element-227" />
                <span data-id="element-228">Garantía 7 días</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-229">
                <CreditCard className="w-4 h-4 text-slate-500" data-id="element-230" />
                <span data-id="element-231">Mercado Pago</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Upsell Modal */}
      {showUpsellModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setShowUpsellModal(false)} data-id="element-232">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm animate-[fadeIn_0.2s_ease-out]" data-id="element-233" />

          {/* Modal */}
          <div className="relative bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl w-full max-w-md animate-[scaleIn_0.25s_ease-out] overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-234">
            {/* Top accent bar */}
            <div className="h-1 bg-gradient-to-r from-orange-500 to-amber-400" data-id="element-235" />

            {/* Close button */}
            <button onClick={() => setShowUpsellModal(false)} className="absolute top-4 right-4 text-slate-400 hover:text-white transition-colors" aria-label="Cerrar" data-id="element-236">
              <X className="w-5 h-5" data-id="element-237" />
            </button>

            <div className="p-6 md:p-8" data-id="element-238">
              {/* Header */}
              <div className="text-center mb-6" data-id="element-239">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-orange-500/10 border border-orange-500/20 mb-4" data-id="element-240">
                  <span className="text-3xl" data-id="element-241">🎁</span>
                </div>
                <h3 className="text-2xl font-black text-white mb-2" data-id="element-242">
                  ¡Oferta especial!
                </h3>
                <p className="text-slate-400 text-sm" data-id="element-243">
                  Antes de continuar, tenemos algo para vos
                </p>
              </div>

              {/* NTICX Upsell Card */}
              <label className={`flex items-start gap-4 p-4 rounded-xl cursor-pointer transition-all duration-300 border-2 mb-6 ${addNticx ? 'bg-orange-500/10 border-orange-500 shadow-[0_0_15px_rgba(249,115,22,0.1)]' : 'bg-slate-800/50 border-slate-700 hover:border-slate-500'}`} onClick={() => setAddNticx(!addNticx)} data-id="element-244">
                {/* Checkbox */}
                <div className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all duration-200 ${addNticx ? 'bg-orange-500 border-orange-500' : 'border-slate-500 bg-transparent'}`} data-id="element-245">
                  {addNticx && <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3} data-id="element-246">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" data-id="element-247" />
                    </svg>}
                </div>

                <div className="flex-1" data-id="element-248">
                  <div className="flex items-center gap-2 mb-1" data-id="element-249">
                    <span className="text-lg" data-id="element-250">📙</span>
                    <h4 className="text-lg font-black text-white" data-id="element-251">NTICX 360</h4>
                  </div>
                  <p className="text-slate-400 text-sm mb-2" data-id="element-252">
                    Clases anuales resueltas de NTICx para complementar tu pack
                  </p>
                  <div className="flex items-center gap-2" data-id="element-253">
                    <span className="text-slate-500 line-through text-sm" data-id="element-254">
                      $19.990
                    </span>
                    <span className="text-xl font-black text-orange-400" data-id="element-255">
                      $9.990
                    </span>
                    <span className="bg-green-600/20 text-green-400 border border-green-600/30 text-[11px] font-bold px-2 py-0.5 rounded-md" data-id="element-256">
                      -50%
                    </span>
                  </div>
                </div>
              </label>

              {/* Continue Button */}
              <button onClick={handleUpsellContinue} className="block w-full font-black py-4 px-8 rounded-xl shadow-lg transform transition-all hover:scale-[1.02] text-center text-lg bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white" data-id="element-257">
                {addNticx ? 'CONTINUAR CON NTICX 360 →' : 'CONTINUAR SIN AGREGAR →'}
              </button>

              {addNticx && <p className="text-center text-green-400 text-sm mt-3 font-medium" data-id="element-258">
                  ✓ NTICX 360 será agregado a tu carrito
                </p>}

              {!addNticx && <p className="text-center text-slate-500 text-xs mt-3" data-id="element-259">
                  Podés agregar NTICX 360 marcando la casilla de arriba
                </p>}
            </div>
          </div>
        </div>}
    </section>;
}